import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Pencil, Plus, Search, Trash2, UserRound } from 'lucide-react';
import { Heading } from '../../layout';
import { useUIColors } from '../../../hooks/useTheme';
import { apiClient, API_BASE_URL } from '../../../services/apiClient';
import {
  createCollaborator,
  deleteCollaborator,
  getCollaborators,
  updateCollaborator,
} from '../../../services/collaboratorsAPI';
import { CHILE_MOBILE_PLACEHOLDER, PHONE_MAX_LENGTH, chileanMobileError, formatChileanMobile } from '../../../utils/textUtils';

const EMPTY_FORM = {
  full_name: '',
  rut: '',
  job_title: '',
  email: '',
  phone: '',
  department: '',
  cost_center: '',
  hire_date: '',
  status: 'Activo',
  user_id: '',
  notes: '',
};

const CollaboratorsWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const inputStyle = {
    height: 34,
    borderRadius: 8,
    border: `1px solid ${ui.inputBorder || ui.border}`,
    background: ui.inputBg || ui.panel,
    color: ui.inputText || ui.text,
    padding: '0 10px',
    fontSize: 13,
    width: '100%',
    boxSizing: 'border-box',
  };
  const primaryBtn = {
    height: 36,
    borderRadius: 8,
    border: 'none',
    background: ui.interactive || '#16a34a',
    color: ui.textOnAccent || '#fff',
    padding: '0 14px',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  };
  const ghostBtn = {
    height: 28,
    borderRadius: 8,
    border: `1px solid ${ui.border}`,
    background: 'transparent',
    color: ui.text,
    padding: '0 8px',
    cursor: 'pointer',
    fontSize: 12,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  };

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      const [collaborators, userRows] = await Promise.all([
        getCollaborators(),
        apiClient(`${API_BASE_URL}/api/users/`).catch(() => []),
      ]);
      setRows(Array.isArray(collaborators) ? collaborators : []);
      setUsers(Array.isArray(userRows) ? userRows : []);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar los colaboradores.');
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const linkedUserIds = useMemo(() => {
    const set = new Set();
    rows.forEach((row) => {
      if (row.user_id && row.id !== editingId) set.add(Number(row.user_id));
    });
    return set;
  }, [rows, editingId]);

  const userOptions = useMemo(
    () =>
      users.filter((user) => {
        const id = Number(user.id);
        if (form.user_id && Number(form.user_id) === id) return true;
        return !linkedUserIds.has(id);
      }),
    [users, linkedUserIds, form.user_id]
  );

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      [
        row.collaborator_code,
        row.full_name,
        row.rut,
        row.job_title,
        row.email,
        row.department,
        row.user_email,
        row.user_code,
        row.status,
      ]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [rows, searchQuery]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      full_name: row.full_name || '',
      rut: row.rut || '',
      job_title: row.job_title || '',
      email: row.email || '',
      phone: row.phone || '',
      department: row.department || '',
      cost_center: row.cost_center || '',
      hire_date: String(row.hire_date || '').slice(0, 10),
      status: row.status || 'Activo',
      user_id: row.user_id ? String(row.user_id) : '',
      notes: row.notes || '',
    });
    setFormError('');
    setShowForm(true);
  };

  const applyUserDefaults = (userId) => {
    const user = users.find((item) => Number(item.id) === Number(userId));
    if (!user) return;
    setForm((prev) => ({
      ...prev,
      user_id: String(userId),
      full_name: prev.full_name || user.full_name || '',
      email: prev.email || user.email || '',
      phone: prev.phone || user.fono || '',
      job_title: prev.job_title || user.job_title || '',
      rut: prev.rut || user.rut || '',
    }));
  };

  const handleSave = async () => {
    if (!String(form.full_name || '').trim()) {
      setFormError('El nombre es obligatorio.');
      return;
    }
    const phoneMessage = chileanMobileError(form.phone);
    if (phoneMessage) {
      setFormError(phoneMessage);
      return;
    }
    const payload = {
      full_name: form.full_name.trim(),
      rut: form.rut.trim(),
      job_title: form.job_title.trim(),
      email: form.email.trim(),
      phone: formatChileanMobile(form.phone).trim(),
      department: form.department.trim(),
      cost_center: form.cost_center.trim(),
      hire_date: form.hire_date || null,
      status: form.status || 'Activo',
      user_id: form.user_id ? Number(form.user_id) : null,
      notes: form.notes.trim(),
    };
    try {
      setIsSaving(true);
      setFormError('');
      if (editingId) await updateCollaborator(editingId, payload);
      else await createCollaborator(payload);
      setShowForm(false);
      await load();
    } catch (err) {
      setFormError(err.message || 'No se pudo guardar.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`¿Eliminar al colaborador ${row.full_name}?`)) return;
    try {
      await deleteCollaborator(row.id);
      await load();
    } catch (err) {
      window.alert(err.message || 'No se pudo eliminar.');
    }
  };

  const th = {
    textAlign: 'left',
    padding: '8px 10px',
    fontSize: 11,
    fontWeight: 700,
    color: ui.muted,
    borderBottom: `1px solid ${ui.border}`,
    whiteSpace: 'nowrap',
  };
  const td = {
    padding: '9px 10px',
    fontSize: 12,
    color: ui.text,
    borderBottom: `1px solid ${ui.border}`,
    verticalAlign: 'top',
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div>
        <Heading level={2} isDarkMode={isDarkMode}>
          Colaboradores
        </Heading>
        <p style={{ margin: '0 0 4px', fontSize: 13, color: ui.muted }}>
          Ficha de personas de la empresa (RUT, cargo, área). Vincula cada colaborador a un usuario
          del sistema para autofill en rendiciones y otros flujos.
        </p>
      </div>

      <div
        style={{
          border: `1px solid ${ui.border}`,
          borderRadius: 12,
          overflow: 'hidden',
          background: ui.page || ui.panel,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: `1px solid ${ui.border}`,
            background: ui.header || ui.panel,
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: ui.text }}>Equipo</h3>
            <p style={{ margin: 0, fontSize: 12, color: ui.muted }}>
              {isLoading
                ? 'Cargando…'
                : error || `${filteredRows.length} colaborador${filteredRows.length === 1 ? '' : 'es'}`}
            </p>
          </div>
          <button type="button" onClick={openCreate} style={primaryBtn}>
            <Plus size={16} /> Nuevo colaborador
          </button>
        </div>

        <div style={{ padding: '10px 16px', borderBottom: `1px solid ${ui.border}` }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, maxWidth: 420 }}>
            <Search size={16} color={ui.muted} aria-hidden />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Nombre, RUT, cargo, usuario…"
              style={{ ...inputStyle, flex: 1 }}
            />
          </label>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
            <thead>
              <tr>
                <th style={th}>Colaborador</th>
                <th style={th}>RUT</th>
                <th style={th}>Cargo</th>
                <th style={th}>Área</th>
                <th style={th}>Usuario vinculado</th>
                <th style={{ ...th, textAlign: 'center' }}>Estado</th>
                <th style={{ ...th, textAlign: 'right' }} />
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} style={{ ...td, textAlign: 'center', color: ui.muted }}>
                    Cargando…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={7} style={{ ...td, textAlign: 'center', color: '#dc2626' }}>
                    {error}
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ ...td, textAlign: 'center', color: ui.muted }}>
                    No hay colaboradores. Crea el primero o vincula usuarios existentes.
                  </td>
                </tr>
              ) : (
                filteredRows.map((row) => (
                  <tr key={row.id}>
                    <td style={td}>
                      <div style={{ fontWeight: 700 }}>{row.full_name}</div>
                      <div style={{ fontSize: 11, color: ui.muted, marginTop: 2 }}>
                        {row.collaborator_code}
                        {row.email ? ` · ${row.email}` : ''}
                      </div>
                    </td>
                    <td style={td}>{row.rut || '—'}</td>
                    <td style={td}>{row.job_title || '—'}</td>
                    <td style={td}>{row.department || '—'}</td>
                    <td style={td}>
                      {row.user_id ? (
                        <>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <UserRound size={14} color={ui.muted} />
                            <span style={{ fontWeight: 600 }}>{row.user_full_name || row.user_code}</span>
                          </div>
                          {row.user_email ? (
                            <div style={{ fontSize: 11, color: ui.muted, marginTop: 2 }}>{row.user_email}</div>
                          ) : null}
                        </>
                      ) : (
                        <span style={{ color: ui.muted }}>Sin vincular</span>
                      )}
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>{row.status}</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: 6 }}>
                        <button type="button" onClick={() => openEdit(row)} style={ghostBtn} title="Editar">
                          <Pencil size={14} />
                        </button>
                        <button type="button" onClick={() => handleDelete(row)} style={ghostBtn} title="Eliminar">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: ui.overlay || 'rgba(0,0,0,0.45)',
            zIndex: 10000,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
          onClick={() => setShowForm(false)}
        >
          <div
            style={{
              width: 520,
              maxWidth: '100%',
              height: '100%',
              background: ui.panel,
              borderLeft: `1px solid ${ui.border}`,
              padding: 20,
              boxSizing: 'border-box',
              display: 'grid',
              gridTemplateRows: 'auto 1fr auto',
              gap: 16,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: ui.text }}>
                {editingId ? 'Editar colaborador' : 'Nuevo colaborador'}
              </p>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: ui.muted }}>
                Los datos de identidad (RUT, cargo) viven aquí; el usuario solo controla el acceso.
              </p>
            </div>

            <div style={{ overflowY: 'auto', display: 'grid', gap: 10, alignContent: 'start' }}>
              <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary || ui.muted }}>
                Nombre completo
                <input
                  value={form.full_name}
                  onChange={(e) => setForm((p) => ({ ...p, full_name: e.target.value }))}
                  style={inputStyle}
                />
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary || ui.muted }}>
                  RUT
                  <input
                    value={form.rut}
                    onChange={(e) => setForm((p) => ({ ...p, rut: e.target.value }))}
                    style={inputStyle}
                    placeholder="12.345.678-9"
                  />
                </label>
                <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary || ui.muted }}>
                  Cargo
                  <input
                    value={form.job_title}
                    onChange={(e) => setForm((p) => ({ ...p, job_title: e.target.value }))}
                    style={inputStyle}
                  />
                </label>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary || ui.muted }}>
                  Email
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                    style={inputStyle}
                  />
                </label>
                <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary || ui.muted }}>
                  Teléfono
                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    maxLength={PHONE_MAX_LENGTH}
                    value={formatChileanMobile(form.phone)}
                    onChange={(e) => setForm((p) => ({ ...p, phone: formatChileanMobile(e.target.value) }))}
                    placeholder={CHILE_MOBILE_PLACEHOLDER}
                    style={inputStyle}
                  />
                </label>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary || ui.muted }}>
                  Área / departamento
                  <input
                    value={form.department}
                    onChange={(e) => setForm((p) => ({ ...p, department: e.target.value }))}
                    style={inputStyle}
                  />
                </label>
                <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary || ui.muted }}>
                  Centro de costo
                  <input
                    value={form.cost_center}
                    onChange={(e) => setForm((p) => ({ ...p, cost_center: e.target.value }))}
                    style={inputStyle}
                  />
                </label>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary || ui.muted }}>
                  Fecha ingreso
                  <input
                    type="date"
                    value={form.hire_date}
                    onChange={(e) => setForm((p) => ({ ...p, hire_date: e.target.value }))}
                    style={inputStyle}
                  />
                </label>
                <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary || ui.muted }}>
                  Estado
                  <select
                    value={form.status}
                    onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                    style={inputStyle}
                  >
                    <option value="Activo">Activo</option>
                    <option value="Inactivo">Inactivo</option>
                  </select>
                </label>
              </div>
              <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary || ui.muted }}>
                Usuario del sistema (opcional)
                <select
                  value={form.user_id}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!value) {
                      setForm((p) => ({ ...p, user_id: '' }));
                      return;
                    }
                    applyUserDefaults(value);
                  }}
                  style={inputStyle}
                >
                  <option value="">Sin vincular</option>
                  {userOptions.map((user) => (
                    <option key={user.id} value={String(user.id)}>
                      {user.full_name} ({user.email})
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary || ui.muted }}>
                Notas
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                  rows={3}
                  style={{
                    ...inputStyle,
                    height: 'auto',
                    padding: '8px 10px',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                  }}
                />
              </label>
              {formError ? <p style={{ margin: 0, fontSize: 12, color: '#dc2626' }}>{formError}</p> : null}
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setShowForm(false)} style={{ ...ghostBtn, height: 34 }}>
                Cancelar
              </button>
              <button type="button" onClick={handleSave} disabled={isSaving} style={primaryBtn}>
                {isSaving ? 'Guardando…' : editingId ? 'Guardar cambios' : 'Crear colaborador'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CollaboratorsWorkspace;
